import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import { MIN_TASK_WIDTH_PX } from '../models/timeline';
import { altKeyLabelKey } from './alt-key';
import { EditDrag } from './timeline-edit.service';
import { TimelineGeometry, xOfDate } from './timeline-geometry';

/**
 * What a drag draws over the timeline. The `ruler` layer hangs off the sticky header and runs the
 * time marker down through every row; the `row` layer sits in the row the block is carried over,
 * in the same coordinates as its blocks.
 */
@Component({
  selector: 'app-timeline-edit-overlay',
  imports: [DatePipe, TranslatePipe],
  templateUrl: './timeline-edit-overlay.component.html',
  styleUrl: './timeline-edit-overlay.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimelineEditOverlayComponent {
  readonly layer = input.required<'ruler' | 'row'>();
  readonly drag = input.required<EditDrag>();
  readonly geometry = input.required<TimelineGeometry>();
  /** How far the ruler layer's marker runs down, the header included. */
  readonly height = input<string>('0px');

  protected readonly ICONS = ICONS;
  protected readonly altKey = altKeyLabelKey();

  protected readonly x = computed(() => xOfDate(this.geometry(), this.drag().time));
  protected readonly width = computed(() => {
    const { time, durationMs } = this.drag();
    const end = new Date(time.getTime() + durationMs);
    return Math.max(MIN_TASK_WIDTH_PX, xOfDate(this.geometry(), end) - this.x());
  });
  protected readonly atWall = computed(() => {
    const { time, earliest } = this.drag();
    return earliest !== null && time.getTime() === earliest.getTime();
  });
  protected readonly wallX = computed(() => {
    const { earliest, shiftMinutes } = this.drag();
    if (earliest === null || (shiftMinutes >= 0 && !this.atWall())) return null;
    return xOfDate(this.geometry(), earliest);
  });
  protected readonly shift = computed(() => {
    const minutes = this.drag().shiftMinutes;
    return minutes > 0 ? `+${minutes}` : minutes < 0 ? `−${-minutes}` : '0';
  });
}
